import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import React from 'react';
import NewsCardNoImage from '../../../../../assets/icons/no-image.svg?reference';
// eslint-disable-next-line no-restricted-imports
import { Link, Markdown } from '../../../../ui';
import themed from '../../../../ui/themer/themed';
import Responsive from '../../../../ui/utils/Responsive';
import type { INewsStory } from '../../../shared/api/contentful/types/INewsStory';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import { getResponsiveImageUrl } from '../../../shared/image/getResponsiveImageUrl';
import { Color } from '../../../shared/styles/Color';

interface IHighlighedNewsStoryProps {
	highlighedNewsStory: INewsStory;
}

export const HighlighedNewsStory = ({
	highlighedNewsStory,
}: IHighlighedNewsStoryProps) => {
	const { t } = useTranslation();
	const contentBody = highlighedNewsStory.summary;

	const headerFirstImage = highlighedNewsStory.images?.[0]?.fields;
	const headerFirstImageUrl = headerFirstImage?.file.url;
	const imageSrc = headerFirstImageUrl || NewsCardNoImage;

	const imageAlt =
		headerFirstImage?.title ||
		headerFirstImage?.description ||
		headerFirstImage?.file?.fileName;

	const { asPath } = useRouter();

	const newsStoryHref = `${asPath}stories/item/${highlighedNewsStory.slug}`;

	return (
		<StyledLink
			href={newsStoryHref}
			aria-label={`Read more about ${highlighedNewsStory.title}`}
			className="hover:!bg-transparent"
		>
			<StyledContainer>
				<StyledImage
					alt={imageAlt}
					src={getHighlightedNewsImageSource(imageSrc)}
				/>
				<StyledContent>
					<StyledLabel>{t('newsroom_top_story')}</StyledLabel>
					<StyledTitle>{highlighedNewsStory.title}</StyledTitle>
					{contentBody && (
						<StyledStoryBody>
							<Markdown source={contentBody} />
						</StyledStoryBody>
					)}
				</StyledContent>
			</StyledContainer>
		</StyledLink>
	);
};

function getHighlightedNewsImageSource(imageSrc: string) {
	return getResponsiveImageUrl(imageSrc, {
		fit: 'thumb',
		w: 670,
		h: 488,
	});
}

const StyledLink = styled(Link)`
	&:hover {
		text-decoration: none;
	}
`;

const StyledContainer = styled.article`
	display: flex;
	flex-direction: row;
	cursor: pointer;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		flex-direction: column;
	}
`;

const StyledImage = styled.img`
	width: 50%;
	height: auto;
	aspect-ratio: 588 / 359;
	object-fit: cover;

	border-top-left-radius: 12px;
	border-bottom-left-radius: 12px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: 100%;
		border-top-right-radius: 12px;
		border-bottom-left-radius: 0;
	}
`;

const StyledContent = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	background-color: rgba(255, 255, 255, 0.1);
	justify-content: center;
	padding: 24px 34px;
	gap: 12px;
	border-top-right-radius: 12px;
	border-bottom-right-radius: 12px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		background-color: ${Color.Surface};
		padding: 16px;
		border-bottom-left-radius: 12px;
		border-top-right-radius: 0;
	}
`;

const StyledLabel = styled.span`
	font-size: 12px;
	font-weight: 500;
	line-height: 14.4px;
	letter-spacing: 1px;
	text-underline-position: from-font;
	text-decoration-skip-ink: none;
	color: ${Color.TextWhite};
	text-transform: uppercase;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		color: ${themed(({ color }) => color.text.default)};
	}
`;

const StyledTitle = styled.h2`
	font-size: 40px;
	font-weight: 700;
	line-height: 48px;
	color: ${Color.TextWhite};
	margin: 0;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: 26px;
		line-height: 31px;
		color: ${themed(({ color }) => color.text.default)};
	}
`;

const StyledStoryBody = styled.div`
	display: -webkit-box;
	color: ${Color.TextWhite};
	line-height: 21px;
	font-size: 18px;
	font-weight: 400;
	max-height: 110px;
	overflow: hidden;
	-webkit-line-clamp: 5;
	-webkit-box-orient: vertical;
	margin-bottom: 0;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		line-height: 20px;
		font-size: 16px;
		color: ${themed(({ color }) => color.text.default)};
	}
`;
